import type { MatchTickerEvent } from '@carouselgg/nexus/match-ticker'
import type { BracketSection } from '@carouselgg/nexus/bracket'
import type { SeasonTimelineSegment } from '@carouselgg/nexus/season-timeline'
import type { RadarChartDataPoint } from '@carouselgg/nexus/radar-chart'

/**
 * Hand-authored demo data for the marketing page's product section.
 *
 * Typed against the real exported interfaces on purpose: if one of those
 * components changes its props, this breaks the build rather than the page.
 *
 * Deliberately not fetched. A real season would make the homepage depend on an
 * API call, render empty in the off-season, and show whatever state the league
 * happens to be in. These teams are invented; the *shapes* are real.
 *
 * The bracket, ticker and standings below are one consistent world — the same
 * eight teams, and results that agree with each other. A league operator is
 * exactly the kind of visitor who will read a screenshot closely.
 */

const DAY = 24 * 60 * 60 * 1000
const now = Date.parse('2026-03-17T20:00:00Z')

/** Quarters and semis are played; the grand final is live. */
export const demoBracket: BracketSection[] = [
  {
    // No section title — the Product section renders its own heading above it.
    rounds: [
      {
        title: 'Quarterfinals',
        matches: [
          {
            id: 'qf1',
            home: { name: 'Nova Collective', seed: 1, score: 2, isWinner: true },
            away: { name: 'Echo Vanguard', seed: 8, score: 0 },
          },
          {
            id: 'qf2',
            home: { name: 'Rift Wardens', seed: 4, score: 2, isWinner: true },
            away: { name: 'Void Signal', seed: 5, score: 1 },
          },
          {
            id: 'qf3',
            home: { name: 'Apex Athletic', seed: 2, score: 2, isWinner: true },
            away: { name: 'Pulse Theory', seed: 7, score: 1 },
          },
          {
            id: 'qf4',
            home: { name: 'Helix Esports', seed: 3, score: 2, isWinner: true },
            away: { name: 'Orbit Nine', seed: 6, score: 0 },
          },
        ],
      },
      {
        title: 'Semifinals',
        matches: [
          {
            id: 'sf1',
            home: { name: 'Nova Collective', seed: 1, score: 2, isWinner: true },
            away: { name: 'Rift Wardens', seed: 4, score: 1 },
          },
          {
            id: 'sf2',
            home: { name: 'Apex Athletic', seed: 2, score: 2, isWinner: true },
            away: { name: 'Helix Esports', seed: 3, score: 0 },
          },
        ],
      },
      {
        title: 'Final',
        matches: [
          {
            id: 'f1',
            home: { name: 'Nova Collective', seed: 1, score: 2 },
            away: { name: 'Apex Athletic', seed: 2, score: 1 },
          },
        ],
      },
    ],
  },
]

/**
 * The ticker agrees with the bracket: the only live match is the final between
 * the two semi winners, and the completed entries are the semis they won.
 */
export const demoMatches: MatchTickerEvent[] = [
  {
    id: 'f1',
    teams: [
      { name: 'Nova Collective', logoUrl: null, designation: 'NOVA', score: 2 },
      { name: 'Apex Athletic', logoUrl: null, designation: 'APEX', score: 1 },
    ],
    date: new Date(now).toISOString(),
    status: 'live',
    game: 'League of Legends',
    division: 'Gold',
    bestOf: 5,
    isStreamed: true,
  },
  {
    id: 'sf1',
    teams: [
      { name: 'Nova Collective', logoUrl: null, designation: 'NOVA', score: 2 },
      { name: 'Rift Wardens', logoUrl: null, designation: 'RIFT', score: 1 },
    ],
    date: new Date(now - 2 * DAY).toISOString(),
    status: 'completed',
    game: 'League of Legends',
    division: 'Gold',
    bestOf: 3,
  },
  {
    id: 'sf2',
    teams: [
      { name: 'Apex Athletic', logoUrl: null, designation: 'APEX', score: 2 },
      { name: 'Helix Esports', logoUrl: null, designation: 'HLX', score: 0 },
    ],
    date: new Date(now - 2 * DAY).toISOString(),
    status: 'completed',
    game: 'League of Legends',
    division: 'Gold',
    bestOf: 3,
  },
  {
    id: 'qf2',
    teams: [
      { name: 'Rift Wardens', logoUrl: null, designation: 'RIFT', score: 2 },
      { name: 'Void Signal', logoUrl: null, designation: 'VOID', score: 1 },
    ],
    date: new Date(now - 5 * DAY).toISOString(),
    status: 'completed',
    game: 'League of Legends',
    division: 'Gold',
    bestOf: 3,
  },
]

export const demoTimeline: SeasonTimelineSegment[] = [
  {
    id: 'reg',
    kind: 'registration',
    startsAt: new Date(now - 84 * DAY).toISOString(),
    endsAt: new Date(now - 70 * DAY).toISOString(),
  },
  {
    id: 'pre',
    kind: 'pre-season',
    startsAt: new Date(now - 70 * DAY).toISOString(),
    endsAt: new Date(now - 63 * DAY).toISOString(),
  },
  {
    id: 'regular',
    kind: 'matches',
    startsAt: new Date(now - 63 * DAY).toISOString(),
    endsAt: new Date(now - 14 * DAY).toISOString(),
    label: 'Regular season',
  },
  {
    id: 'playoffs',
    kind: 'playoffs',
    startsAt: new Date(now - 14 * DAY).toISOString(),
    endsAt: new Date(now + 3 * DAY).toISOString(),
  },
]

export const demoSeasonNow = new Date(now).toISOString()

/**
 * Trust Score evidence. Axes are the smurf-detection signals; `value` is the
 * applicant and `baseline` the bracket median.
 */
export const demoVerifiedRadar: RadarChartDataPoint[] = [
  { stat: 'Performance', value: 52, baseline: 50 },
  { stat: 'Peak gap', value: 46, baseline: 50 },
  { stat: 'Climb rate', value: 55, baseline: 50 },
  { stat: 'Champ shift', value: 48, baseline: 50 },
  { stat: 'Dormancy', value: 44, baseline: 50 },
  { stat: 'Duo pattern', value: 51, baseline: 50 },
]

export const demoSmurfRadar: RadarChartDataPoint[] = [
  { stat: 'Performance', value: 94, baseline: 50 },
  { stat: 'Peak gap', value: 88, baseline: 50 },
  { stat: 'Climb rate', value: 91, baseline: 50 },
  { stat: 'Champ shift', value: 73, baseline: 50 },
  { stat: 'Dormancy', value: 62, baseline: 50 },
  { stat: 'Duo pattern', value: 69, baseline: 50 },
]

/**
 * Scores and levels follow the real ladder in api/internal/auth/trust_score.go:
 * Veteran >= 80, Established >= 50, Verified >= 20, Basic >= 1, Unverified 0.
 */
export const demoApplicants = [
  {
    id: 'verified',
    handle: 'kestrel_ow',
    score: 88,
    level: 'Veteran',
    detail: 'Gold II · 340 ranked games · 2 accounts linked · 3 seasons played',
    verdict: 'Cleared',
    radar: demoVerifiedRadar,
    tone: 'good' as const,
    evidence:
      'Plays at their real rank. Performance, climb rate and champion pool all sit within a standard deviation of the bracket median.',
  },
  {
    id: 'smurf',
    handle: 'nullsmith',
    score: 14,
    level: 'Basic',
    detail: 'Gold IV · 22 ranked games · account age 11 days · no linked accounts',
    verdict: 'Smurf risk',
    radar: demoSmurfRadar,
    tone: 'bad' as const,
    evidence:
      'Diamond-tier performance on a Gold IV account. Peak-rank gap and climb rate are each more than four standard deviations from the bracket median.',
  },
]

export const demoStandings = [
  { pos: 1, team: 'Nova Collective', record: '11–1', diff: '+18', up: true },
  { pos: 2, team: 'Apex Athletic', record: '10–2', diff: '+14', up: true },
  { pos: 3, team: 'Helix Esports', record: '8–4', diff: '+6', up: true },
  { pos: 4, team: 'Rift Wardens', record: '7–5', diff: '+1', up: true },
  { pos: 5, team: 'Void Signal', record: '5–7', diff: '−4', up: false },
]
